import React, { useEffect, useRef, useState } from 'react';
import {
  Animated,
  Dimensions,
  Modal,
  ScrollView,
  TouchableWithoutFeedback,
  View,
} from 'react-native';
import { Divider } from 'react-native-elements';
import { Button } from '@/components/ui/therapist/Button';
import { Icon } from '@/components/Icon';
import { Text } from '@/components/Text';
import colors from '@/config/colors';
import { styles } from './styles';

const { width } = Dimensions.get('window');

interface FilterDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: () => void;
  onReset: () => void;
  children: React.ReactNode;
  drawerWidth?: number;
  title?: string;
  applyLabel?: string;
  resetLabel?: string;
}

export const FilterDrawer: React.FC<FilterDrawerProps> = ({
  isOpen,
  onClose,
  onApply,
  onReset,
  children,
  drawerWidth = width * 0.45,
  title = 'Filter',
  applyLabel = 'Filter anwenden',
  resetLabel = 'Filter zurücksetzen',
}) => {
  const [visible, setVisible] = useState<boolean>(isOpen);

  const translateX = useRef(new Animated.Value(drawerWidth)).current;

  useEffect(() => {
    if (isOpen) {
      setVisible(true);
      Animated.timing(translateX, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }).start();
      return;
    }
    Animated.timing(translateX, {
      toValue: drawerWidth,
      duration: 300,
      useNativeDriver: true,
    }).start(() => {
      setVisible(false);
    });
  }, [isOpen, drawerWidth, translateX]);

  if (!visible) {
    return null;
  }

  return (
    <Modal
      supportedOrientations={['landscape', 'landscape-left', 'landscape-right']}
      visible
      transparent
      animationType="none"
      statusBarTranslucent
    >
      <TouchableWithoutFeedback onPress={onClose}>
        <View style={styles.touchBlocker} />
      </TouchableWithoutFeedback>
      <Animated.View
        style={[
          styles.drawer,
          {
            width: drawerWidth,
            transform: [{ translateX }],
          },
        ]}
      >
        <ScrollView>
          <View style={styles.headerContainer}>
            <View style={styles.header}>
              <View style={styles.titleRow}>
                <Icon
                  name="filter"
                  size={24}
                  color={colors.newUI.therapist.textBlue100}
                />
                <Text style={styles.title}>{title}</Text>
              </View>
              <Icon
                name="close"
                size={24}
                onPress={onClose}
                color={colors.newUI.therapist.textBlue100}
              />
            </View>
            <View style={styles.headerDivider}>
              <Divider width={1} color={colors.newUI.therapist.primaryBlue10} />
            </View>
          </View>

          {children}

          <View style={styles.footer}>
            <Button
              look="primary"
              style={styles.button}
              onPress={onApply}
              label={applyLabel}
            />
            <Button
              look="secondary"
              style={styles.button}
              onPress={onReset}
              label={resetLabel}
            />
          </View>
        </ScrollView>
      </Animated.View>
    </Modal>
  );
};
